import {
  MagnifyingGlass, CaretLeft, CaretRight, CalendarBlank,
  ArrowsDownUp, CaretUp, CaretDown, DotsThree, Plus, Phone,
  ArrowClockwise, X, CheckCircle,
} from '@phosphor-icons/react';
import StatusBadge from '../components/redesign/StatusBadge';
import { EntityChip } from '../components/redesign/EntityChip';
import PriorityChip from '../components/redesign/PriorityChip';
import HubspotBadge from '../components/redesign/HubspotBadge';
import { Button } from '../components/redesign/ui';
import { TICKET_STATUS_LABELS } from '../constants/ticketOptions';
import { COMPLAINT_GROUPS, COMPLAINT_GROUP_LABEL } from '../constants/ticketOptions';

// Inline priority filter vocab (matches today's hardcoded dropdown).
const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

// Channel presentation — ported from the live TicketList CHANNEL_CFG.
const CHANNEL_CFG = {
  email:        { cls: 'bg-blue-100 text-blue-700',     label: '\u{1F4E7} Email' },
  portal:       { cls: 'bg-gray-100 text-gray-600',     label: '\u{1F5A5} Portal' },
  phone:        { cls: 'bg-purple-100 text-purple-700', label: '\u{1F4DE} Phone' },
  whatsapp:     { cls: 'bg-green-100 text-green-700',   label: '\u{1F4AC} WhatsApp' },
  instagram:    { cls: 'bg-pink-100 text-pink-700',     label: '\u{1F4F8} Instagram' },
  instagram_dm: { cls: 'bg-pink-100 text-pink-700',     label: '\u{1F4F8} Instagram' },
  app:          { cls: 'bg-indigo-100 text-indigo-700', label: '\u{1F4F1} App' },
  twitter:      { cls: 'bg-sky-100 text-sky-700',       label: '\u{1F426} Twitter' },
  walkin:       { cls: 'bg-amber-100 text-amber-700',   label: '\u{1F6B6} Walk-in' },
};

function ChannelBadge({ channel }) {
  const key = (channel || '').toLowerCase().replace(/[-\s]/g, '');
  const cfg = CHANNEL_CFG[key];
  if (!cfg) return channel
    ? <span className="text-xs text-ink-faint">{channel}</span>
    : <span className="text-ink-faint text-xs">—</span>;
  return <span className={`inline-flex items-center rounded-chip px-2 py-0.5 text-[11px] font-medium ${cfg.cls}`}>{cfg.label}</span>;
}

// Native <select> styled to the redesign token language (the handoff Select is a
// display-only stub, so we use a real select for functional filtering).
function FilterSelect({ value, onChange, children, ariaLabel }) {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-field border border-paper-border2 bg-paper px-3 py-2 text-[13px] text-ink outline-none focus:border-ink"
    >
      {children}
    </select>
  );
}

// Sortable header cell. Arrow only on sortable columns (Priority/Status/Updated).
function SortTh({ label, col, sortBy, sortOrder, onSort }) {
  const active = sortBy === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className={`flex items-center gap-1 text-left ${active ? 'text-ink' : 'text-ink-faint'}`}
    >
      {label}
      {active
        ? (sortOrder === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)
        : <ArrowsDownUp size={12} />}
    </button>
  );
}

const COLS = 'grid grid-cols-[110px_150px_96px_1fr_130px_112px_92px_150px_84px_36px] gap-3 items-center';

/**
 * @param {{
 *   rows: Array<{ _key:string, id:string, entity_slug:string, entityName:string,
 *     subject:string, group:string, groupLabel:string, priority:string|null,
 *     status:string, hubspot:boolean, customer:{name:string,mobile:string},
 *     channel:string, updatedRelative:string, updatedAbsolute:string, raw:object }>,
 *   total:number, page:number, pageSize:number, offset:number, loading:boolean,
 *   filters:{status,priority,complaint_group,entity_id,date_from,date_to,search:string},
 *   onFilterChange:(key:string,value:string)=>void, onClearFilters:()=>void,
 *   searchInput:string, onSearchInputChange:(v:string)=>void,
 *   dateDraft:{date_from:string,date_to:string}, onDateDraftChange:(key,value)=>void,
 *   sortBy:'created_at'|'priority'|'status', sortOrder:'asc'|'desc', onSort:(col)=>void,
 *   entities:Array<{id:number,name:string,slug:string}>, currentUserRole:string,
 *   contactMobile:string, contactId:string, contactNameDisplay:string,
 *   contactLookupStatus:'idle'|'loading'|'found'|'not_found',
 *   onContactMobileChange:(v:string)=>void, onLookupContact:()=>void, onClearContactLookup:()=>void,
 *   onOpenTicket:(row)=>void, onNewTicket:()=>void, onPage:(dir:'prev'|'next')=>void, onRefresh:()=>void,
 * }} props
 */
export default function TicketListView({
  rows = [], total = 0, page = 1, pageSize = 50, offset = 0, loading = false,
  filters = {}, onFilterChange, onClearFilters,
  searchInput = '', onSearchInputChange,
  dateDraft = { date_from: '', date_to: '' }, onDateDraftChange,
  sortBy = 'created_at', sortOrder = 'desc', onSort,
  entities = [], currentUserRole,
  contactMobile = '', contactId, contactNameDisplay = '', contactLookupStatus = 'idle',
  onContactMobileChange, onLookupContact, onClearContactLookup,
  onOpenTicket, onNewTicket, onPage, onRefresh,
}) {
  const isSuperAdmin = currentUserRole === 'super_admin';

  const hsFilterActive = Boolean(
    filters.status || filters.priority || filters.entity_id ||
    filters.complaint_group || filters.date_from || filters.date_to
  );
  const hsOnlyCount = rows.filter((r) => r.raw?.source === 'hubspot').length;
  const hsSuffix = !hsFilterActive && hsOnlyCount > 0 ? ` (+${hsOnlyCount} HubSpot-only)` : '';

  const countLine = loading
    ? 'Loading…'
    : total === 0
      ? `${rows.length} ticket${rows.length !== 1 ? 's' : ''}${hsSuffix}`
      : `${offset + 1}–${offset + rows.length} of ${total} tickets${hsSuffix}`;

  // Active-filter chips (each removable) — Linear/Attio-style summary strip.
  const priorityLabel = (v) => (PRIORITY_OPTIONS.find((p) => p.value === v)?.label || v);
  const groupLabel = (v) => (COMPLAINT_GROUP_LABEL[v] || v);
  const entityLabel = (v) => (entities.find((e) => String(e.id) === String(v))?.name || v);
  const chips = [];
  if (filters.search) chips.push({ key: 'search', text: `“${filters.search}”`, onRemove: () => onSearchInputChange('') });
  if (filters.status) {
    // Multi-status form (e.g. 'resolved,closed' from the /pending-action deep
    // link) — split and label each part so the chip reads "Resolved, Closed"
    // instead of the raw comma-joined slug. Single values keep the existing
    // lookup path via the same map.
    const statusText = filters.status.includes(',')
      ? filters.status.split(',').map(s => TICKET_STATUS_LABELS[s.trim()] || s.trim()).join(', ')
      : (TICKET_STATUS_LABELS[filters.status] || filters.status);
    chips.push({ key: 'status', text: `Status: ${statusText}`, onRemove: () => onFilterChange('status', '') });
  }
  if (filters.priority) chips.push({ key: 'priority', text: `Priority: ${priorityLabel(filters.priority)}`, onRemove: () => onFilterChange('priority', '') });
  if (filters.complaint_group) chips.push({ key: 'group', text: `Group: ${groupLabel(filters.complaint_group)}`, onRemove: () => onFilterChange('complaint_group', '') });
  if (isSuperAdmin && filters.entity_id) chips.push({ key: 'entity', text: `Entity: ${entityLabel(filters.entity_id)}`, onRemove: () => onFilterChange('entity_id', '') });
  if (filters.date_from) chips.push({ key: 'from', text: `From: ${filters.date_from}`, onRemove: () => onDateDraftChange('date_from', '') });
  if (filters.date_to) chips.push({ key: 'to', text: `To: ${filters.date_to}`, onRemove: () => onDateDraftChange('date_to', '') });
  if (contactLookupStatus === 'found' && contactNameDisplay) chips.push({ key: 'contact', text: `Customer: ${contactNameDisplay}`, onRemove: () => onClearContactLookup() });
  const anyActive = chips.length > 0;

  return (
    <div className="p-1">
      <div className="overflow-hidden rounded-card border border-paper-border bg-paper-panel shadow-soft">

        {/* Header + filter bar */}
        <div className="flex flex-col gap-4 border-b border-paper-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl text-ink">Tickets</h1>
              <p className="mt-0.5 text-[13px] text-ink-muted">{countLine}</p>
            </div>
            <Button onClick={onNewTicket}><Plus size={15} weight="bold" />New ticket</Button>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search */}
            <div className="flex w-64 items-center gap-2 rounded-field border border-paper-border2 bg-paper px-3 py-2 text-ink-faint">
              <MagnifyingGlass size={15} />
              <input
                aria-label="Search tickets"
                placeholder="Search ticket #, name, mobile, subject…"
                value={searchInput}
                onChange={(e) => onSearchInputChange(e.target.value)}
                className="w-full bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-faint"
              />
            </div>

            {/* Dropdown filters */}
            <FilterSelect ariaLabel="Filter by status" value={filters.status || ''} onChange={(v) => onFilterChange('status', v)}>
              <option value="">All statuses</option>
              {Object.entries(TICKET_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </FilterSelect>

            <FilterSelect ariaLabel="Filter by priority" value={filters.priority || ''} onChange={(v) => onFilterChange('priority', v)}>
              <option value="">All priorities</option>
              {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </FilterSelect>

            <FilterSelect ariaLabel="Filter by complaint group" value={filters.complaint_group || ''} onChange={(v) => onFilterChange('complaint_group', v)}>
              <option value="">All groups</option>
              {COMPLAINT_GROUPS.map((g) => <option key={g.slug} value={g.slug}>{g.label}</option>)}
            </FilterSelect>

            {isSuperAdmin && entities.length > 0 && (
              <FilterSelect ariaLabel="Filter by entity" value={filters.entity_id || ''} onChange={(v) => onFilterChange('entity_id', v)}>
                <option value="">All entities</option>
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </FilterSelect>
            )}

            {/* Date range */}
            <div className="flex items-center gap-1.5 rounded-field border border-paper-border2 bg-paper px-2.5 py-1.5 text-ink-faint">
              <CalendarBlank size={14} />
              <input
                type="date" aria-label="Date from" min="2020-01-01"
                value={dateDraft.date_from}
                onChange={(e) => onDateDraftChange('date_from', e.target.value)}
                className="bg-transparent text-[12px] text-ink outline-none"
              />
              <span className="text-ink-faint">–</span>
              <input
                type="date" aria-label="Date to" min="2020-01-01"
                value={dateDraft.date_to}
                onChange={(e) => onDateDraftChange('date_to', e.target.value)}
                className="bg-transparent text-[12px] text-ink outline-none"
              />
            </div>

            {/* Mobile -> contact lookup group */}
            <div className="flex items-center gap-2 border-l border-paper-border pl-2.5">
              <div className={`flex items-center gap-2 rounded-field border px-3 py-2 ${
                contactLookupStatus === 'found' ? 'border-status-resolved/40 bg-status-resolved/5'
                : contactLookupStatus === 'not_found' ? 'border-paper-border2 bg-paper-muted'
                : 'border-paper-border2 bg-paper'}`}>
                <Phone size={14} className="text-ink-faint" />
                <input
                  type="tel" aria-label="Look up customer by mobile"
                  placeholder="Mobile — Enter to look up"
                  value={contactMobile}
                  onChange={(e) => onContactMobileChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onLookupContact(); } }}
                  className="w-44 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-faint"
                />
                {contactLookupStatus === 'loading' && <ArrowClockwise size={14} className="animate-spin text-ink-faint" />}
                {contactLookupStatus === 'found' && <CheckCircle size={14} weight="fill" className="text-status-resolved" />}
              </div>
              {contactLookupStatus === 'found' && contactNameDisplay && (
                <span className="inline-flex items-center gap-1.5 rounded-chip bg-status-resolved/10 px-2.5 py-1 text-[12px] font-medium text-status-resolved">
                  {contactNameDisplay}
                  <button aria-label="Clear customer lookup" onClick={onClearContactLookup}><X size={12} /></button>
                </span>
              )}
              {contactLookupStatus === 'not_found' && <span className="text-[12px] text-ink-faint">No contact found</span>}
            </div>

            {/* Refresh — icon-only, far right, separated */}
            <button
              type="button" aria-label="Refresh tickets" onClick={onRefresh}
              className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted"
            >
              <ArrowClockwise size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Active-filters summary strip */}
        {anyActive && (
          <div className="flex flex-wrap items-center gap-2 border-b border-paper-border bg-paper-muted/40 px-6 py-2.5">
            {chips.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1.5 rounded-chip border border-paper-border2 bg-paper-panel px-2.5 py-1 text-[12px] text-ink-muted">
                {c.text}
                <button aria-label={`Remove ${c.key} filter`} onClick={c.onRemove} className="text-ink-faint hover:text-ink"><X size={12} /></button>
              </span>
            ))}
            <button onClick={onClearFilters} className="ml-auto rounded-field px-2.5 py-1 text-[12px] font-medium text-ink-muted hover:bg-paper-muted">
              Clear all
            </button>
          </div>
        )}

        {/* Table header */}
        <div className={`${COLS} border-b border-paper-border px-6 py-3 text-[11px] font-semibold uppercase tracking-wide text-ink-faint`}>
          <span>Ticket</span>
          <span>Customer</span>
          <span>Entity</span>
          <span>Subject</span>
          <span>Group</span>
          <span>Channel</span>
          <SortTh label="Priority" col="priority" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
          <SortTh label="Status" col="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
          <SortTh label="Updated" col="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
          <span />
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-ink-faint">
            <ArrowClockwise size={26} className="animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <p className="font-serif text-lg text-ink">No tickets found</p>
            <p className="mt-1 text-[13px] text-ink-muted">Try adjusting your filters.</p>
          </div>
        ) : (
          rows.map((t, i) => (
            <div
              key={t._key || t.id}
              onClick={() => onOpenTicket?.(t)}
              className={`cursor-pointer hover:bg-paper-muted/50 ${i < rows.length - 1 ? 'border-b border-paper-border' : ''}`}
            >
              <div className={`${COLS} px-6 py-4`}>
                {/* Ticket */}
                <span className="text-[13px] font-medium text-ink-muted">{t.id || '—'}</span>
                {/* Customer */}
                <span className="min-w-0">
                  {t.customer?.name
                    ? <span className="block truncate text-[13px] text-ink" dir="auto">{t.customer.name}</span>
                    : t.customer?.mobile
                      ? <span className="block truncate text-[13px] text-ink-muted">{t.customer.mobile}</span>
                      : <span className="text-ink-faint text-xs">—</span>}
                  {t.customer?.name && t.customer?.mobile && (
                    <span className="block truncate text-[11px] text-ink-faint">{t.customer.mobile}</span>
                  )}
                </span>
                {/* Entity */}
                <span>{t.entity_slug ? <EntityChip entitySlug={t.entity_slug} /> : <span className="text-ink-faint text-xs">—</span>}</span>
                {/* Subject */}
                <span className="truncate text-sm text-ink" dir="auto">{t.subject || '—'}</span>
                {/* Group */}
                <span className="truncate text-[13px] text-ink-muted">{t.groupLabel || '—'}</span>
                {/* Channel */}
                <span><ChannelBadge channel={t.channel} /></span>
                {/* Priority */}
                <span>{t.priority ? <PriorityChip priority={t.priority} /> : <span className="text-xs text-ink-faint">—</span>}</span>
                {/* Status */}
                <span className="flex items-center gap-1.5">
                  {t.hubspot && !t.status ? <HubspotBadge /> : (t.status ? <StatusBadge status={t.status} /> : <span className="text-ink-faint text-xs">—</span>)}
                </span>
                {/* Updated */}
                <span className="text-xs text-ink-faint" title={t.updatedAbsolute || ''}>{t.updatedRelative || '—'}</span>
                {/* Overflow affordance */}
                <DotsThree size={18} className="text-ink-faint" />
              </div>
            </div>
          ))
        )}

        {/* Pagination footer */}
        {!loading && total > pageSize && (
          <div className="flex items-center justify-between border-t border-paper-border px-6 py-4">
            <span className="text-[13px] text-ink-muted">{offset + 1}–{offset + rows.length} of {total}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onPage?.('prev')} disabled={offset === 0}
                className="inline-flex items-center gap-1.5 rounded-field border border-paper-border2 px-3 py-1.5 text-[13px] text-ink-muted disabled:opacity-40"
              ><CaretLeft size={13} />Prev</button>
              <button
                onClick={() => onPage?.('next')} disabled={offset + pageSize >= total}
                className="inline-flex items-center gap-1.5 rounded-field border border-paper-border2 px-3 py-1.5 text-[13px] text-ink disabled:opacity-40"
              >Next<CaretRight size={13} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
